import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { Alert } from "../../components/ui/Alert";
import { AppIcon } from "../../components/ui/AppIcon";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { FormField } from "../../components/ui/FormField";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { Switch } from "../../components/ui/Switch";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import type { Platform } from "../../lib/api/devices";
import {
  type AppGroupMemberRow,
  type AppGroupRow,
  appGroupsApi,
} from "../../lib/api/network/appGroups";
import { downloadCsv, rowsToCsv } from "../../lib/csv";
import { formatMessage, t } from "../../lib/i18n";
import { useRecompileNotice } from "../../lib/recompileNotice";
import { useLocale } from "../../lib/useLocale";

interface AppGroupsPageProps {
  embedded?: boolean;
}

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t("errors." + err.code, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function AppGroupsPage({ embedded = false }: AppGroupsPageProps) {
  useLocale();
  const qc = useQueryClient();
  const notice = useRecompileNotice();
  const { push: pushToast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editRow, setEditRow] = useState<AppGroupRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<AppGroupRow | null>(null);
  const [selected, setSelected] = useState<AppGroupRow | null>(null);

  const list = useQuery({
    queryKey: ["network", "app-groups"],
    queryFn: appGroupsApi.list,
  });

  const create = useMutation({
    mutationFn: () => appGroupsApi.create({ name, description }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["network", "app-groups"] });
      setCreateOpen(false);
      setName("");
      setDescription("");
    },
  });

  const editMut = useMutation({
    mutationFn: (vars: {
      id: string;
      body: { name?: string; description?: string; is_active?: boolean };
    }) => appGroupsApi.patch(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["network", "app-groups"] });
      setEditRow(null);
      notice();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => appGroupsApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["network", "app-groups"] });
      setPendingDelete(null);
      notice();
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("errors.generic"), body: translateError(err) }),
  });

  function openEdit(row: AppGroupRow) {
    setEditRow(row);
    setEditName(row.name);
    setEditDescription(row.description);
    setEditActive(row.is_active);
  }

  function exportCsv() {
    if (!list.data) return;
    const csv = rowsToCsv(list.data, [
      { header: "name", value: (row) => row.name },
      { header: "description", value: (row) => row.description },
      { header: "is_default", value: (row) => row.is_default },
      { header: "is_active", value: (row) => row.is_active },
      { header: "members_count", value: (row) => row.members_count },
      { header: "rule_usage_count", value: (row) => row.rule_usage_count },
      { header: "created_at", value: (row) => row.created_at },
    ]);
    downloadCsv("app-groups-" + new Date().toISOString().slice(0, 10) + ".csv", csv);
  }

  const columns: Column<AppGroupRow>[] = [
    {
      key: "name",
      header: t("network.appGroups.column.name"),
      render: (row) => (
        <button
          type="button"
          onClick={() => setSelected(row)}
          style={{
            background: "none",
            border: "none",
            color: "var(--accent)",
            cursor: "pointer",
            padding: 0,
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          <div>{row.name}</div>
          {row.description && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              {row.description}
            </div>
          )}
        </button>
      ),
    },
    {
      key: "default",
      header: t("network.appGroups.column.default"),
      render: (row) =>
        row.is_default ? (
          <span className="badge badge--info">{t("network.appGroups.defaultBadge")}</span>
        ) : (
          "-"
        ),
    },
    {
      key: "members",
      header: t("network.appGroups.column.members"),
      render: (row) => <span className="num">{row.members_count}</span>,
      align: "right",
    },
    {
      key: "rule_usage",
      header: t("network.appGroups.column.usedInRules"),
      render: (row) =>
        row.rule_usage_count > 0 ? (
          <Link
            to={"/policies/rules?allowedAppGroup=" + encodeURIComponent(row.id)}
            className="badge badge--info"
            style={{ textDecoration: "none" }}
          >
            {row.rule_usage_count} {t("network.appGroups.rulesShort")}
          </Link>
        ) : (
          <span className="app-pkg">{t("network.appGroups.notUsed")}</span>
        ),
      align: "right",
    },
    {
      key: "active",
      header: t("network.appGroups.column.active"),
      render: (row) =>
        row.is_active ? (
          <span className="badge badge--success">{t("common.yes")}</span>
        ) : (
          <span className="badge">{t("common.no")}</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <Button variant="secondary" size="sm" onClick={() => setSelected(row)}>
            {t("network.appGroups.action.manage")}
          </Button>
          {!row.is_default && (
            <>
              <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                {t("network.appGroups.action.edit")}
              </Button>
              <Button variant="danger" size="sm" onClick={() => setPendingDelete(row)}>
                {t("network.appGroups.action.delete")}
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const actions = (
    <>
      <Button variant="secondary" onClick={exportCsv} disabled={!list.data}>
        {t("network.appGroups.action.exportCsv")}
      </Button>
      <Button onClick={() => setCreateOpen(true)}>
        {t("network.appGroups.action.create")}
      </Button>
    </>
  );

  return (
    <div>
      {!embedded && (
        <PageHeader
          eyebrow={t("network.appGroups.eyebrow")}
          title={t("network.appGroups.title")}
          subtitle={t("network.appGroups.subtitle")}
          actions={actions}
        />
      )}
      {embedded && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12,
            gap: 8,
          }}
        >
          {actions}
        </div>
      )}

      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState titleKey="states.errorTitle" message={translateError(list.error)} />
        )}
        {list.data && (
          <Table
            rows={list.data}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                titleKey="network.appGroups.emptyTitle"
                bodyKey="network.appGroups.emptyBody"
              />
            }
          />
        )}
      </Card>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("network.appGroups.create.title")}
      >
        <form
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!name.trim()) return;
            create.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          {create.error && <Alert tone="danger">{translateError(create.error)}</Alert>}
          <FormField label={t("network.appGroups.create.field.name")} required>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t("network.appGroups.create.field.description")}>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            )}
          </FormField>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="ghost" type="button" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t("network.appGroups.create.submit")}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={editRow !== null}
        onClose={() => setEditRow(null)}
        title={
          editRow
            ? formatMessage("network.appGroups.edit.title", { name: editRow.name })
            : ""
        }
      >
        {editRow && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              editMut.mutate({
                id: editRow.id,
                body: {
                  name: editName,
                  description: editDescription,
                  is_active: editActive,
                },
              });
            }}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            {editMut.error && <Alert tone="danger">{translateError(editMut.error)}</Alert>}
            <FormField label={t("network.appGroups.create.field.name")} required>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                  required
                />
              )}
            </FormField>
            <FormField label={t("network.appGroups.create.field.description")}>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                />
              )}
            </FormField>
            <Switch
              checked={editActive}
              onChange={setEditActive}
              label={t("common.active")}
              sub={!editActive ? t("network.appGroups.notUsed") : undefined}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button variant="ghost" type="button" onClick={() => setEditRow(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={editMut.isPending}>
                {t("network.appGroups.edit.submit")}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={
          pendingDelete
            ? formatMessage("network.appGroups.delete.title", {
                name: pendingDelete.name,
              })
            : ""
        }
        body={
          pendingDelete
            ? pendingDelete.rule_usage_count > 0
              ? formatMessage("network.appGroups.delete.body", {
                  usage: String(pendingDelete.rule_usage_count),
                })
              : t("network.appGroups.delete.bodyNoUsage")
            : ""
        }
        confirmLabel={t("network.appGroups.delete.confirm")}
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected
            ? formatMessage("network.appGroups.manage.title", { name: selected.name })
            : ""
        }
        size="lg"
      >
        {selected && (
          <AppGroupMembersPanel
            group={selected}
            onError={(message) =>
              pushToast({
                tone: "danger",
                title: t("errors.generic"),
                body: message,
              })
            }
          />
        )}
      </Modal>
    </div>
  );
}

function AppGroupMembersPanel({
  group,
  onError,
}: {
  group: AppGroupRow;
  onError: (message: string) => void;
}) {
  const qc = useQueryClient();
  const notice = useRecompileNotice();
  const [packageId, setPackageId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [platform, setPlatform] = useState<Platform>("android");

  const members = useQuery({
    queryKey: ["network", "app-groups", group.id, "members"],
    queryFn: () => appGroupsApi.listMembers(group.id),
  });

  const invalidateMembers = () => {
    void qc.invalidateQueries({
      queryKey: ["network", "app-groups", group.id, "members"],
    });
    void qc.invalidateQueries({ queryKey: ["network", "app-groups"] });
  };

  const add = useMutation({
    mutationFn: () =>
      appGroupsApi.addMember(group.id, {
        package_id: packageId.trim(),
        display_name: displayName.trim(),
        platform,
      }),
    onSuccess: () => {
      invalidateMembers();
      setPackageId("");
      setDisplayName("");
      notice();
    },
    onError: (error) => onError(translateError(error)),
  });

  const remove = useMutation({
    mutationFn: (memberId: string) => appGroupsApi.removeMember(group.id, memberId),
    onSuccess: () => {
      invalidateMembers();
      notice();
    },
    onError: (error) => onError(translateError(error)),
  });

  const columns: Column<AppGroupMemberRow>[] = [
    {
      key: "app",
      header: t("network.appGroups.member.column.app"),
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AppIcon name={row.display_name} packageId={row.package_id} size={28} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              {row.display_name || row.package_id}
            </span>
            <span className="mono" style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              {row.package_id}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "platform",
      header: t("network.appGroups.member.column.platform"),
      render: (row) => (
        <span className="badge">
          {t("common.platform." + row.platform, row.platform || "-")}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => remove.mutate(row.id)}
          loading={remove.isPending}
        >
          {t("network.appGroups.member.action.remove")}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Alert tone="info">{t("network.appGroups.member.authoredHint")}</Alert>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (packageId.trim()) add.mutate();
        }}
        className="stack"
      >
        <FormField
          label={t("network.appGroups.member.add.packageId")}
          hint={t("network.appGroups.member.add.packageHint")}
          required
        >
          {(id) => (
            <Input
              id={id}
              value={packageId}
              onChange={(event) => setPackageId(event.target.value)}
              placeholder={t("network.appGroups.member.add.packagePlaceholder")}
              autoCapitalize="none"
              autoCorrect="off"
              required
            />
          )}
        </FormField>
        <FormField label={t("network.appGroups.member.add.displayName")}>
          {(id) => (
            <Input
              id={id}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder={t("network.appGroups.member.add.displayNamePlaceholder")}
            />
          )}
        </FormField>
        <FormField label={t("network.appGroups.member.add.platform")}>
          {(id) => (
            <Select
              id={id}
              value={platform}
              onChange={(event) => setPlatform(event.target.value as Platform)}
            >
              <option value="android">{t("common.platform.android")}</option>
              <option value="ios">{t("common.platform.ios")}</option>
            </Select>
          )}
        </FormField>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button type="submit" loading={add.isPending} disabled={!packageId.trim()}>
            {t("network.appGroups.member.add.submit")}
          </Button>
        </div>
      </form>

      <Alert tone="info">{t("network.appGroups.member.modeHint")}</Alert>
      {members.isLoading && <LoadingState />}
      {members.error && (
        <ErrorState titleKey="states.errorTitle" message={translateError(members.error)} />
      )}
      {members.data && (
        <Table
          rows={members.data}
          columns={columns}
          rowKey={(row) => row.id}
          empty={
            <EmptyState
              titleKey="network.appGroups.member.emptyTitle"
              bodyKey="network.appGroups.member.emptyBody"
            />
          }
        />
      )}
    </div>
  );
}
